import axios from "axios";
import apiService from "@src/api_service";
import { ApiServiceErrorCallBack } from "@src/api_service/interface";
import { cn } from "@src/utils/app_functions";
import {
  AdminRef,
  ApiErrorBody,
  LinkCurrency,
  LinkType,
  SchoolPaymentLink,
  SchoolPaymentLinkEventAction,
  SchoolPaymentLinkStatus,
  SchoolPlanOption,
} from "./interface";

export const LINK_STATUSES: SchoolPaymentLinkStatus[] = ['active', 'paid', 'expired', 'cancelled'];

export const LINK_TYPE_LABELS: Record<LinkType, string> = {
  subscription: 'Plan subscription',
  onboarding: 'Onboarding fee',
};

export const isOnboardingLink = (link: Pick<SchoolPaymentLink, 'linkType'>) => link.linkType === 'onboarding';

// One line describing what a link pays for, e.g. "Gold · 420 students · yearly" or
// "One-time onboarding fee · 5 student slots".
export const linkPaysFor = (link: SchoolPaymentLink) =>
  isOnboardingLink(link)
    ? `One-time onboarding fee · ${(link.numberOfStudents ?? 0).toLocaleString()} student slots`
    : `${link.planName ?? 'Plan'} · ${(link.numberOfStudents ?? 0).toLocaleString()} students · ${link.billingCycle ?? ''}`;

export const STATUS_STYLES: Record<SchoolPaymentLinkStatus, string> = {
  active: 'bg-green-100 text-green-800',
  paid: 'bg-emerald-600 text-white',
  expired: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-700',
};

export const StatusPill = ({ status }: { status: SchoolPaymentLinkStatus }) => (
  <span className={cn("px-3 py-1 rounded-full text-xs font-medium capitalize", STATUS_STYLES[status])}>
    {status}
  </span>
);

export const EVENT_LABELS: Record<SchoolPaymentLinkEventAction, string> = {
  created: 'Link created',
  email_sent: 'Email sent',
  email_failed: 'Email failed',
  resent: 'Email resent',
  opened: 'Opened after closing',
  checkout_started: 'Checkout started',
  checkout_failed: 'Checkout failed',
  paid: 'Paid',
  reconciled: 'Payment confirmed (webhook missed)',
  expired: 'Expired',
  cancelled: 'Cancelled',
};

export const EVENT_DOT: Record<SchoolPaymentLinkEventAction, string> = {
  created: 'bg-[#E55109]',
  email_sent: 'bg-blue-500',
  resent: 'bg-blue-500',
  email_failed: 'bg-red-500',
  opened: 'bg-gray-400',
  checkout_started: 'bg-amber-500',
  checkout_failed: 'bg-red-500',
  paid: 'bg-emerald-600',
  reconciled: 'bg-emerald-600',
  expired: 'bg-gray-400',
  cancelled: 'bg-red-500',
};

export const formatMoney = (amount: number, currency: LinkCurrency | string = 'NGN') => {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount);
  } catch {
    return `${currency} ${amount.toLocaleString()}`;
  }
};

export const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';

export const adminName = (admin?: AdminRef | string | null) => {
  if (!admin) return undefined;
  if (typeof admin === 'string') return admin;
  return admin.fullname || admin.email || admin._id;
};

export const authHeaders = (json = true) => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  ...(json ? { 'Content-Type': 'application/json' } : {}),
});

export const errorMessage = (err: ApiServiceErrorCallBack<ApiErrorBody>, fallback: string) =>
  err?.errorData?.message || err?.errorMsg || fallback;

// The admin API has no school plan listing, so read the plans from the public third-party route,
// which lives next to /v1/admin on the same host.
export const fetchSchoolPlans = async (): Promise<SchoolPlanOption[]> => {
  const base = apiService.appApiBaseUrl.replace(/\/v1\/admin\/?$/, '/v1/thirdparty');
  const response = await axios.get<{ data: SchoolPlanOption[] }>(`${base}/school_plans`);
  return Array.isArray(response.data?.data) ? response.data.data : [];
};

export const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};
